import { useEffect, useRef } from 'react';
import { useRealtime } from './use-realtime';
import type { RealtimeTopicLifecycleOptions } from '../../types/topic-lifecycle.types';

/**
 * Owns view subscription lifetime and signals opportunities for domain recovery.
 * @param options - Stable topics, recovery callback and fallback interval.
 */
export const useRealtimeTopicLifecycle = ({
  topics,
  onRecover,
  unavailableRecoveryIntervalMs,
}: RealtimeTopicLifecycleOptions): void => {
  const { subscribe, status, topicStatuses } = useRealtime();
  const readyTopicsRef = useRef<Set<string>>(new Set());
  const previousStatusRef = useRef(status);

  useEffect(() => {
    if (topics.length === 0) return;
    return subscribe(topics);
  }, [subscribe, topics]);

  const hasUnavailableTopics = topics.some(
    (topic) => topicStatuses[topic] === 'unavailable'
  );
  useEffect(() => {
    if (!hasUnavailableTopics) return;
    onRecover();
    const timer = window.setInterval(onRecover, unavailableRecoveryIntervalMs);
    return () => window.clearInterval(timer);
  }, [hasUnavailableTopics, onRecover, unavailableRecoveryIntervalMs]);

  useEffect(() => {
    const ready = new Set(
      topics.filter((topic) => topicStatuses[topic] === 'subscribed')
    );
    if ([...ready].some((topic) => !readyTopicsRef.current.has(topic))) {
      onRecover();
    }
    readyTopicsRef.current = ready;
  }, [topics, topicStatuses, onRecover]);

  useEffect(() => {
    const wasDisconnected =
      previousStatusRef.current === 'offline' ||
      previousStatusRef.current === 'reconnecting';
    previousStatusRef.current = status;
    if (status === 'connected' && wasDisconnected) onRecover();
  }, [onRecover, status]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onRecover();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [onRecover]);
};
